import React, { useState, useEffect } from 'react';
import _get from 'lodash/get';
import { subMonths, differenceInDays } from 'date-fns';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import {
    renderFamilyMemberName,
    getGoalsParentDeploymentId,
    getDailyTrackingParentDeploymentId,
} from '../../helpers/helpers.js';
import Report from './Report.js';
import familyMembersQuery from './familyMembers.graphql';
import goalsQuery from './goals.graphql';
import style from './style.css';

const Fortress = window.cccisd.fortress;

const TrackingReport = props => {
    const [isLoading, setIsLoading] = useState(true);
    const [pawnInfo, setPawnInfo] = useState(null);
    const [familyMemberPickerInfo, setFamilyMemberPickerInfo] = useState(null);
    const [isLoadingReportData, setIsLoadingReportData] = useState(false);
    const [goalsParentDeploymentId, setGoalsParentDeploymentId] = useState(null);
    const [dailyTrackingParentDeploymentId, setDailyTrackingParentDeploymentId] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [numOfDays, setNumOfDays] = useState(30);
    const now = new Date();

    useEffect(() => {
        async function getFamilyPickerInfo() {
            const resp = await client.query({
                query: familyMembersQuery,
                variables: {
                    familyId: Fortress.user.acting.group.id,
                    familyRandomHash: Fortress.user.acting.group.random_hash,
                },
                fetchPolicy: 'network-only',
            });

            setFamilyMemberPickerInfo(
                (_get(resp, 'data.roles.anyRoleList', []) || []).filter(member => {
                    if (Fortress.user.acting.role.handle === 'tfhChild') {
                        return member.pawn.pawnId === Fortress.user.acting.id;
                    }
                    return true;
                })
            );
        }

        if (props.pawnId && props.pawnHash && props.displayName) {
            setPawnInfo({ ...props });
        } else if (Fortress.user.acting.role.handle === 'tfhChild') {
            const self = { pawn: { ...Fortress.user.acting }, user: { ...Fortress.user } };
            setPawnInfo({
                pawnId: Fortress.user.acting.id,
                pawnHash: Fortress.user.acting.random_hash,
                displayName: renderFamilyMemberName(self, [self]),
            });
            getFamilyPickerInfo();
        } else {
            getFamilyPickerInfo();
        }

        setIsLoading(false);
    }, []);

    async function fetchReportData() {
        if (!pawnInfo) {
            return;
        }

        setIsLoadingReportData(true);
        let gDeploymentId = goalsParentDeploymentId;
        if (!gDeploymentId) {
            gDeploymentId = await getGoalsParentDeploymentId();
            setGoalsParentDeploymentId(gDeploymentId);
        }

        let dtDeploymentId = dailyTrackingParentDeploymentId;
        if (!dtDeploymentId) {
            dtDeploymentId = await getDailyTrackingParentDeploymentId();
            setDailyTrackingParentDeploymentId(dtDeploymentId);
        }

        const reportResp = await client.query({
            query: goalsQuery,
            variables: {
                pawnId: pawnInfo.pawnId,
                pawnHash: pawnInfo.pawnHash,
                goalsParentDeploymentId: gDeploymentId,
                dailyTrackingParentDeploymentId: dtDeploymentId,
            },
            fetchPolicy: 'network-only',
        });
        const d = _get(reportResp, 'data.roles.anyRole', {}) || {};
        delete d.__typename;
        setReportData(d);
        setIsLoadingReportData(false);
    }

    useEffect(() => {
        fetchReportData();
    }, [pawnInfo]);

    function renderDateRangePicker() {
        return (
            <div className="col-auto">
                <label className="form-label" htmlFor="numDays">
                    Date range:
                </label>
                <select
                    id="numDays"
                    name="numDays"
                    value={numOfDays}
                    className="form-select"
                    style={{ width: 'fit-content', minWidth: '200px' }}
                    aria-label="Number of Days"
                    onChange={event => {
                        const val = parseInt(event.target.value, 10);
                        setNumOfDays(val);
                    }}
                >
                    {[7, 30, 60].map(num => (
                        <option key={num + 'd'} value={num}>
                            {num} days
                        </option>
                    ))}
                    {[3, 6].map(num => (
                        <option key={num + 'mo'} value={differenceInDays(now, subMonths(now, num))}>
                            {num} months
                        </option>
                    ))}
                </select>
            </div>
        );
    }

    function getCsvUrl() {
        if (!reportData) {
            return;
        }

        let csv =
            'Date,"GREEN Food","RED Food","GREEN Activity","RED Activity",Calories,Weight,"Family Check-In",Praise\n';
        reportData.dailyTracking
            .sort((apA, apB) => {
                if (apA.devTags.dailyTrackingDate && apB.devTags.dailyTrackingDate) {
                    return apB.devTags.dailyTrackingDate.localeCompare(apA.devTags.dailyTrackingDate);
                }
                if (apB.devTags.dailyTrackingDate) {
                    return 1;
                }
                return -1;
            })
            .forEach(ap => {
                const { devTags } = ap;
                let row = '';
                if (devTags?.dailyTrackingDate) {
                    row += devTags.dailyTrackingDate;
                }
                row += ',';

                if (devTags?.dailyTrackingGreen || devTags?.dailyTrackingGreen === 0) {
                    row += devTags.dailyTrackingGreen;
                }
                row += ',';

                if (devTags?.dailyTrackingRed || devTags?.dailyTrackingRed === 0) {
                    row += devTags.dailyTrackingRed;
                }
                row += ',';

                if (devTags?.dailyTrackingActivity || devTags?.dailyTrackingActivity === 0) {
                    row += devTags.dailyTrackingActivity;
                }
                row += ',';

                if (devTags?.dailyTrackingRedActivity || devTags?.dailyTrackingRedActivity === 0) {
                    row += devTags.dailyTrackingRedActivity;
                }
                row += ',';

                if (devTags?.dailyTrackingCalories || devTags?.dailyTrackingCalories === 0) {
                    row += devTags.dailyTrackingCalories;
                }
                row += ',';

                if (devTags?.dailyTrackingWeighAmount || devTags?.dailyTrackingWeighAmount === 0) {
                    row += devTags.dailyTrackingWeighAmount;
                }
                row += ',';

                if (devTags?.dailyTrackingCheckIn && devTags?.dailyTrackingCheckIn === 'checked') {
                    row += 'Yes';
                }
                row += ',';

                if (devTags?.dailyTrackingPraise && devTags?.dailyTrackingPraise === 'checked') {
                    row += 'Yes';
                }

                csv += `${row}\n`;
            });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        return URL.createObjectURL(blob);
    }

    function renderFamilyMemberPicker() {
        if (!familyMemberPickerInfo || !Array.isArray(familyMemberPickerInfo)) {
            return <div className="row">{renderDateRangePicker()}</div>;
        }

        return (
            <div className="row">
                <div className="col-auto">
                    <label className="form-label" htmlFor="familyMember">
                        Choose family member:
                    </label>
                    <select
                        id="familyMember"
                        name="familyMember"
                        value={pawnInfo?.pawnId || -1}
                        className={`form-select ${!pawnInfo && 'fw-light fst-italic text-body-tertiary'}`}
                        style={{ width: 'fit-content', minWidth: '300px' }}
                        aria-label="Family Member"
                        onChange={event => {
                            const memberInfo = familyMemberPickerInfo.find(
                                mem => parseInt(mem.pawn.pawnId, 10) === parseInt(event.target.value, 10)
                            );
                            setPawnInfo({
                                displayName: renderFamilyMemberName(memberInfo, familyMemberPickerInfo),
                                pawnId: memberInfo.pawn.pawnId,
                                pawnHash: memberInfo.pawn.pawnHash,
                            });
                        }}
                    >
                        {!pawnInfo && (
                            <option value="-1" disabled>
                                -- choose family member --
                            </option>
                        )}
                        {familyMemberPickerInfo.map(p => (
                            <option key={p.pawn.pawnId} value={p.pawn.pawnId}>
                                {renderFamilyMemberName(p, familyMemberPickerInfo)}
                            </option>
                        ))}
                    </select>
                </div>
                {renderDateRangePicker()}
            </div>
        );
    }

    function renderCsvButton() {
        if (!reportData || !pawnInfo) {
            return <span />;
        }

        return (
            <div className="mt-5 mb-3">
                <a
                    className={'btn btn-link link-offset-3 ' + style.dottedUnderline}
                    href={getCsvUrl()}
                    download={`Daily Tracking - ${pawnInfo.displayName}.csv`}
                >
                    Download CSV of all data
                </a>
            </div>
        );
    }

    function renderReport() {
        if (!pawnInfo) {
            return null;
        }

        if (isLoadingReportData || !reportData) {
            return (
                <div className="my-3">
                    <Loader type="inline" loading />
                </div>
            );
        }

        return <Report reportData={reportData} displayName={pawnInfo?.displayName} numOfDays={numOfDays} />;
    }

    return (
        <Loader loading={isLoading}>
            {renderFamilyMemberPicker()}
            {renderCsvButton()}
            {renderReport()}
        </Loader>
    );
};

export default TrackingReport;
